import { useState, type KeyboardEvent, type ReactNode } from 'react'
import { Button } from '@/components/Button'

import styles from './EditableMessageContent.module.css'
import { TextArea as TextAreaInput } from '@/components/inputs/TextArea'

export interface EditableMessageContentProps {
  allowEdit: boolean
  isEditing: boolean
  text: string
  isSavePending: boolean
  onStartEdit: () => unknown
  onSave: (text: string) => unknown
  onCancel: () => unknown
  children: ReactNode
}

export function EditableMessageContent({
  allowEdit,
  isEditing,
  text,
  isSavePending,
  onStartEdit,
  onSave,
  onCancel,
  children,
}: EditableMessageContentProps) {
  const [isHover, setIsHover] = useState(false)
  const displayEditMessageButton = allowEdit && !isEditing && isHover

  const saveEdition = async (newText: string) => {
    await onSave(newText)
    // since after saving the message, the hover state is still true, but we might be out of the container, we need to set it to false
    setIsHover(false)
  }

  const cancelEdition = () => {
    onCancel()
    // since after cancelling the message, the hover state is still true, but we might be out of the container, we need to set it to false
    setIsHover(false)
  }

  return (
    <div
      className={styles.editMessageContainer}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <EditMessageButton
        onPress={() => onStartEdit()}
        isVisible={displayEditMessageButton}
      />
      {!isEditing ? (
        children
      ) : (
        <TextEditingContent
          text={text}
          onCancel={cancelEdition}
          onSave={saveEdition}
          isPending={isSavePending}
        />
      )}
    </div>
  )
}

interface TextEditingContentProps {
  text: string
  onCancel: () => void
  onSave: (text: string) => void
  isPending: boolean
}

function TextEditingContent({
  text,
  onCancel,
  onSave,
  isPending,
}: TextEditingContentProps) {
  const [value, setValue] = useState(text)

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      if (!isPending) {
        onSave(value)
      }
    }
  }

  return (
    <div className={styles.textEditContainer}>
      <TextAreaInput
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className={styles.textEditInput}
        autoSize
        onKeyDown={handleKeyDown}
      />
      <div className={styles.textEditButtons}>
        <Button label="Cancel" variant="default" onPress={onCancel} />
        <Button
          label="Save"
          variant="primary"
          isDisabled={value.length === 0}
          isPending={isPending}
          onPress={() => onSave(value)}
        />
      </div>
    </div>
  )
}

interface EditMessageButtonProps {
  onPress: () => void
  isVisible: boolean
}

function EditMessageButton({ onPress, isVisible }: EditMessageButtonProps) {
  return isVisible ? (
    <div className={styles.editButtonContainer}>
      <Button
        label="Edit"
        variant="default"
        iconRight="chat-edit"
        iconSize={30}
        className={styles.editButton}
        onPress={onPress}
      />
    </div>
  ) : null
}
